import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { cityBySlug, countyBySlug, nearbyCities } from '@/data/seo/locations';
import { SEO_TRADES, MONEY_TRADES } from '@/data/seo/trades';
import { MONEY_PAGES } from '@/data/seo/moneyPages';
import { breadcrumbSchema, faqSchema, localBusinessSchema, locationFaqs, itemListSchema } from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money } from '@/data/constants';
import LiveProjects from '@/components/seo/LiveProjects';

const CityPage: React.FC = () => {
  const { slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const city = cityBySlug(slug);
  if (!city) return <NotFound />;
  const county = countyBySlug(city.county);

  const faqs = locationFaqs(city.name, 'city', lang);
  const url = `/${lang}/california/${city.slug}`;

  const tradeLinks = SEO_TRADES.slice(0, 24).map((tr) => ({
    label: lang === 'es' ? tr.es : tr.en,
    to: path(`/trades/${tr.slug}`),
  }));
  const leadLinks = MONEY_PAGES.filter((m) => m.citySlug === city.slug)
    .map((m) => ({ label: `${m.trade.en} Leads ${m.place}`, to: path(`/leads/${m.slug}`) }));
  const nearby = nearbyCities(city).map((c) => ({ label: `${c.name}, CA`, to: path(`/california/${c.slug}`) }));

  const intro =
    lang === 'es'
      ? `Proyectos de construcción activos en ${city.name}, California. Propietarios y contratistas generales publican el trabajo gratis; los subcontratistas con membresía desbloquean la ubicación exacta, los contactos y los planos por ${money(LEAD_PRICE)}.`
      : `Active construction projects in ${city.name}, California. Homeowners and general contractors post work for free; subcontractors with a membership unlock the exact location, contacts and plan documents for ${money(LEAD_PRICE)}.`;

  return (
    <>
      <Seo
        title={lang === 'es' ? `Construcción en ${city.name}, CA — Proyectos y Leads` : `${city.name}, CA Construction Projects & Contractor Leads`}
        description={
          lang === 'es'
            ? `Oportunidades de construcción en ${city.name}, ${county?.name ?? ''} County, CA. Todos los oficios, leads a ${money(LEAD_PRICE)}, publicar es gratis.`
            : `Construction opportunities in ${city.name}, ${county?.name ?? ''} County, CA. Every trade, flat ${money(LEAD_PRICE)} leads, free to post a project.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'California', url: `/${lang}/california` },
            ...(county ? [{ name: `${county.name} County`, url: `/${lang}/california/counties/${county.slug}` }] : []),
            { name: city.name, url },
          ]),
          localBusinessSchema({ name: `${city.name}, CA`, area: `${city.name}, California`, url }),
          faqSchema(faqs),
          itemListSchema(`${city.name} trade pages`, tradeLinks.slice(0, 20).map((x) => ({ name: x.label, url: x.to }))),
        ]}
      />

      <PageHero eyebrow={t('seo.cityEyebrow')} title={`${city.name}, California`} intro={intro}>
        <Link to={path('/jobs')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaBrowse')}
        </Link>
        <Link to={path('/register')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('seo.ctaPostBtn')}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: 'California', url: path('/california') },
            ...(county ? [{ name: `${county.name} County`, url: path(`/california/counties/${county.slug}`) }] : []),
            { name: city.name },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: t('seo.factCity'), v: `${city.name}, CA` },
            { k: t('seo.factCounty'), v: county ? `${county.name} County` : '—' },
            { k: t('seo.factRegion'), v: county?.region || 'California' },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
          ]}
        />

        <Section title={t('seo.constructionActivity')}>
          <p>{intro}</p>
          <p>{t('seo.cityBody', { city: city.name, county: county ? `${county.name} County` : 'California' })}</p>
        </Section>

        <Section title={t('seo.popularTrades', { city: city.name })}>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {MONEY_TRADES.slice(0, 12).map((tr) => (
              <li key={tr.slug}>
                <Link
                  to={path(`/trades/${tr.slug}`)}
                  className="block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-orange-300 hover:text-orange-600"
                >
                  {lang === 'es' ? tr.es : tr.en}
                </Link>
              </li>
            ))}
          </ul>
          <TrustStrip />
        </Section>

        <LiveProjects heading={t('seo.recentProjects', { place: `${city.name}, CA` })} />

        <FaqSection faqs={faqs} />

        {leadLinks.length > 0 && <LinkGrid title={t('seo.leadPagesPlace', { place: `${city.name}, CA` })} links={leadLinks} icon="map" />}
        <LinkGrid title={t('seo.nearbyCities')} links={nearby} icon="map" />
        <LinkGrid title={t('seo.allTrades')} links={tradeLinks} icon="trade" />

        <CtaBand variant="post" />
      </PageWrap>
    </>
  );
};

export default CityPage;
